import { AlertCircle, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

export interface Blocker {
  message: string;
  severity: 'error' | 'warning';
  moduleKey?: string | undefined;
}

interface BlockerListProps {
  blockers: Blocker[];
  title?: string | undefined;
  collapsible?: boolean | undefined;
  defaultExpanded?: boolean | undefined;
}

export function BlockerList({
  blockers,
  title = 'Issues',
  collapsible = false,
  defaultExpanded = true,
}: BlockerListProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const errors = blockers.filter((b) => b.severity === 'error');
  const warnings = blockers.filter((b) => b.severity === 'warning');

  if (blockers.length === 0) {
    return null;
  }

  const header = (
    <div
      className={cn(
        'flex items-center gap-2',
        collapsible && 'cursor-pointer hover:bg-muted/50 -mx-2 px-2 py-1 rounded'
      )}
      onClick={collapsible ? () => setExpanded(!expanded) : undefined}
      role={collapsible ? 'button' : undefined}
      aria-expanded={collapsible ? expanded : undefined}
    >
      {collapsible && (
        <span className="text-muted-foreground">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      )}
      <span className="font-medium text-sm">{title}</span>
      <span className="text-xs text-muted-foreground">
        ({errors.length} {errors.length === 1 ? 'error' : 'errors'}, {warnings.length}{' '}
        {warnings.length === 1 ? 'warning' : 'warnings'})
      </span>
    </div>
  );

  return (
    <div className="space-y-2">
      {header}

      {(!collapsible || expanded) && (
        <ul className="space-y-2">
          {errors.map((blocker, index) => (
            <li
              key={`error-${index}`}
              className="flex items-start gap-2 text-sm rounded-md bg-destructive/10 text-destructive p-2"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                {blocker.moduleKey && (
                  <span className="font-medium">[{blocker.moduleKey}] </span>
                )}
                {blocker.message}
              </div>
            </li>
          ))}

          {warnings.map((blocker, index) => (
            <li
              key={`warning-${index}`}
              className="flex items-start gap-2 text-sm rounded-md bg-warning/10 text-warning-foreground p-2"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                {blocker.moduleKey && (
                  <span className="font-medium">[{blocker.moduleKey}] </span>
                )}
                {blocker.message}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
